import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    useTheme,
    Avatar,
    Chip,
    IconButton,
    Stack,
    Dialog,
    DialogTitle,
    Button,
    DialogContent,
    DialogActions,
} from '@mui/material';
import PropTypes from 'prop-types';
import { PinDrop, Edit, Delete, Visibility, CheckCircle } from '@mui/icons-material';
import ProductForm from './AddProductForm';
import { UPLOAD_IMAGES_API_URL } from '../constant';
import { gql, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { set } from 'react-hook-form';

const UPDATE_PRODUCT = gql`
  mutation UpdateProduct(
    $product_id: ID!
    $product_name: String
    $product_description: String
  ) {
    updateProduct(
      product_id: $product_id
      product_name: $product_name
      product_description: $product_description
    ) {
      success
      message
    }
  }
`;

const DELETE_PRODUCT = gql`
  mutation DeleteProduct($product_id: ID!) {
    deleteProduct(product_id: $product_id){
        success
      message
    }
  }
`;

export default function ProductCard({ product, onClick, role, user, refresh }) {
    const { t , i18n} = useTranslation();
    const theme = useTheme();
    const [editProduct, setEditProduct] = useState(product);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [deleteProduct] = useMutation(DELETE_PRODUCT);
    const [updateProduct, { loading: uploadLoading, error: uploadError }] = useMutation(UPDATE_PRODUCT);
    const handleUpdateProduct = async (formData) => {
        setUploading(true);
        try {
            // Update product details
            await updateProduct({
                variables: {
                    product_id: formData.product_id,
                    product_name: formData.product_name,
                    product_description: formData.product_description
                }
            });

            // Handle image update if changed
            if (formData.product_image) {
                const formDataImage = new FormData();
                formDataImage.append("image", formData.product_image);

                await fetch(`${UPLOAD_IMAGES_API_URL}/upload/product/${user.eventId}/${user.role}/${formData.product_id}`, {
                    method: 'POST',
                    body: formDataImage,
                });
            }

            setSuccessMessage(t('productUpdatedSuccessfully'));
            setEditProduct(null);
            refresh();
            setFormOpen(false);
            setTimeout(() => {
                setSuccessMessage('');
            }, 2000);
        } catch (error) {
            setErrorMessage(t('errorUpdatingProduct'));
        } finally {
            setUploading(false);
        }
    };

    const onEdit = (product) => {
        setEditProduct(product);
        setFormOpen(true);
    }
    const onDelete = (product) => {
        setProductToDelete(product);
        setDeleteConfirmOpen(true);
    }
    return (
        <Paper
            elevation={3}
            sx={{
                position: 'relative',
                p: 2,
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                direction: 'ltr', // Ensure LTR direction
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[6]
                }
            }}
            onClick={onClick}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }} >
                <img
                    src={product.product_image}
                    style={{
                        width: '100%',
                        height: 200,
                        borderRadius: 2,
                        objectFit: 'contain'
                    }}
                />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="600">
                        {product.product_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {product.product_description?.substring(0, 100)}...
                    </Typography>

                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Stack direction={"row"} spacing={1}>
                            <Avatar
                                src={product.profile_pic || './profile_pic.png'}
                                sx={{ width: 50, height: 50 }}
                            />
                            <Stack direction={"column"} spacing={0}>
                                <Typography variant="body2">
                                    {product.name} · {product.designation}
                                </Typography>
                                <Chip
                                    icon={<PinDrop fontSize="small" />}
                                    label={`${t('StallNo')}: ${product.stallNo}`}
                                    size="small"
                                    sx={{ ml: 0 }}
                                />
                            </Stack>
                        </Stack>
                    </Box>
                </Box>
            </Box>

            <ProductForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSubmit={handleUpdateProduct}
                product={editProduct}
                uploading={uploading}
                errorMessage={errorMessage}
                successMessage={successMessage}
                user={user}
                uploadLoading={uploadLoading}
                uploadError={uploadError}
                mode='edit'
            />
            <Dialog open={deleteConfirmOpen} dir={i18n.dir()} onClose={() => setDeleteConfirmOpen(false)} >
                <DialogTitle>{t('ConfirmDelete')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('AreYouSureYouWantToDeleteThisProduct')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button sx={{mx:1}} onClick={() => setDeleteConfirmOpen(false)}>{t('Cancel')}</Button>
                    <Button
                        color="error"
                        onClick={async () => {
                            try {
                                await deleteProduct({
                                    variables: { product_id: productToDelete.id }
                                }).then(() => {
                                    setDeleteConfirmOpen(false);
                                    setSuccessMessage(t('productDeletedSuccessfully'));
                                    setTimeout(() => {
                                        refresh();
                                        setSuccessMessage('');
                                    }, 2000);
                                }
                                );

                            } catch (error) {
                                console.error('Delete error:', error);
                            }
                        }}
                        sx={{mx:1}}
                    >
                        {t('Delete')}
                    </Button>
                </DialogActions>
            </Dialog>
            {successMessage && (
                <Box sx={{
                    position: 'absolute',
                    top: 1,
                    left: 2,
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'no-wrap',
                    gap: 1,
                    backgroundColor: 'green',
                    borderRadius: 1,
                    p: 0.5,
                    boxShadow: 1,
                    width: 'fit-content'
                }}>
                    <Box sx={{ color: 'white', display:'flex', alignItems:'center', gap:1 }}>
                        <CheckCircle fontSize="small" />
                        <Typography color={"white"} variant="caption">{successMessage}</Typography>


                    </Box>
                </Box>
            )}

        </Paper>
    );
};

ProductCard.propTypes = {
    product: PropTypes.object.isRequired,
    onClick: PropTypes.func.isRequired,
    role: PropTypes.string.isRequired
};